import { useLiveQuery } from "dexie-react-hooks";
import { ChevronDown, ChevronRight, Pencil, Plus, Star, StarOff, Tag, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BUILTIN_FILTERS, colorHex, runFilter, validateFilter, type Filter, type Label } from "@todoer/shared";
import { confirmDialog, Dialog, Menu } from "../components/overlay.js";
import { sortTasks, TaskList } from "../components/TaskList.js";
import { Button, ColorPicker, cx, EmptyState, Field, IconButton, Input, PageHeader, SectionHeader, SegmentedControl, Switch, useIsPhone } from "../components/ui.js";
import { db } from "../lib/db.js";
import { dueLabel } from "../lib/format.js";
import { useActiveTasks, useFilterContext } from "../lib/hooks.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";

function Row({ to, color, name, query, count, favorite, menu, icon }: {
  to: string; color: string; name: string; query?: string; count: number; favorite?: boolean; menu?: React.ReactNode; icon?: "tag";
}) {
  return (
    <li className="group flex min-h-11 items-center gap-1 border-b border-border">
      <Link to={to} className="flex min-w-0 flex-1 items-center gap-3 rounded-control px-1 py-2 hover:bg-hover phone:min-h-12">
        {icon === "tag" ? <Tag size={16} aria-hidden style={{ color: colorHex(color) }} /> : <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorHex(color) }} />}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body">{name}</span>
          {query && <span className="block truncate font-mono text-meta text-text-faint">{query}</span>}
        </span>
        {favorite && <Star size={14} className="shrink-0 text-p2" fill="currentColor" aria-label="Favourite" />}
        <span className="tabular w-8 shrink-0 text-right text-meta text-text-faint">{count || ""}</span>
      </Link>
      {menu ?? <span className="w-8 phone:w-11" />}
    </li>
  );
}

export function FiltersLabels() {
  const filters = useLiveQuery(() => db.filters.toArray(), [], []);
  const labels = useLiveQuery(() => db.labels.toArray(), [], []);
  const tasks = useActiveTasks() ?? [];
  const ctx = useFilterContext();
  const isPhone = useIsPhone();
  const [tab, setTab] = useState<"filters" | "labels">("filters");
  const [editFilter, setEditFilter] = useState<Filter | "new" | null>(null);
  const [editLabel, setEditLabel] = useState<Label | "new" | null>(null);

  const count = (query: string) => {
    if (!ctx) return 0;
    try { return runFilter(query, tasks, ctx).reduce((n, g) => n + g.tasks.length, 0); } catch { return 0; }
  };

  const filtersSection = (
    <section aria-labelledby="filters-h">
      <SectionHeader id="filters-h" title="My filters" count={filters.length} actions={<IconButton icon={Plus} label="Add filter" onClick={() => setEditFilter("new")} />} />
      {filters.length ? (
        <ul>
          {[...filters].sort((a, b) => a.itemOrder - b.itemOrder).map((f) => (
            <Row key={f.id} to={`/filter/${f.id}`} color={f.color} name={f.name} query={f.query} count={count(f.query)} favorite={f.isFavorite}
              menu={<Menu size="sm" label={`${f.name} actions`} items={[
                { label: "Edit", icon: Pencil, onSelect: () => setEditFilter(f) },
                { label: f.isFavorite ? "Remove from favourites" : "Add to favourites", icon: f.isFavorite ? StarOff : Star, onSelect: () => void dispatch(cmd("filter_update", { id: f.id, isFavorite: !f.isFavorite })) },
                { label: "Delete", icon: Trash2, danger: true, divider: true, onSelect: async () => { if (await confirmDialog({ title: `Delete “${f.name}”?`, confirmLabel: "Delete", danger: true })) void dispatch(cmd("filter_delete", { id: f.id })); } },
              ]} />} />
          ))}
        </ul>
      ) : (
        <EmptyState compact kind="filters" title="No saved filters yet" action={<Button icon={Plus} onClick={() => setEditFilter("new")}>Add filter</Button>}>
          Save searches like “p1 & #Work” or “assigned to: me & 7 days”.
        </EmptyState>
      )}
      <h3 className="mb-1 mt-6 text-caption font-semibold uppercase text-text-faint">Suggested</h3>
      <ul>
        {BUILTIN_FILTERS.map((f) => <Row key={f.id} to={`/filter/${f.id}`} color={f.color} name={f.name} query={f.query} count={count(f.query)} />)}
      </ul>
    </section>
  );

  const labelsSection = (
    <section aria-labelledby="labels-h">
      <SectionHeader id="labels-h" title="Labels" count={labels.length} actions={<IconButton icon={Plus} label="Add label" onClick={() => setEditLabel("new")} />} />
      {labels.length ? (
        <ul>
          {[...labels].sort((a, b) => a.itemOrder - b.itemOrder || a.name.localeCompare(b.name)).map((l) => (
            <Row key={l.id} to={`/label/${l.id}`} color={l.color} name={`@${l.name}`} icon="tag" favorite={l.isFavorite} count={tasks.filter((t) => t.labelIds.includes(l.id)).length}
              menu={<Menu size="sm" label={`@${l.name} actions`} items={[
                { label: "Edit", icon: Pencil, onSelect: () => setEditLabel(l) },
                { label: l.isFavorite ? "Remove from favourites" : "Add to favourites", icon: l.isFavorite ? StarOff : Star, onSelect: () => void dispatch(cmd("label_update", { id: l.id, isFavorite: !l.isFavorite })) },
                { label: "Delete", icon: Trash2, danger: true, divider: true, onSelect: async () => { if (await confirmDialog({ title: `Delete @${l.name}?`, body: "It will be removed from all tasks.", confirmLabel: "Delete", danger: true })) void dispatch(cmd("label_delete", { id: l.id })); } },
              ]} />} />
          ))}
        </ul>
      ) : (
        <EmptyState compact kind="label" title="No labels yet" action={<Button icon={Plus} onClick={() => setEditLabel("new")}>Add label</Button>}>
          Type @name when adding a task to create one.
        </EmptyState>
      )}
    </section>
  );

  return (
    <div>
      <PageHeader title="Filters & Labels" />
      {isPhone ? (
        <>
          <SegmentedControl label="Show" value={tab} onChange={setTab} className="mb-4 flex w-full"
            options={[{ value: "filters", label: "Filters" }, { value: "labels", label: "Labels" }]} />
          {tab === "filters" ? filtersSection : labelsSection}
        </>
      ) : (
        <div className="grid gap-10 xl:grid-cols-2">{filtersSection}{labelsSection}</div>
      )}
      {editFilter && <FilterDialog filter={editFilter === "new" ? undefined : editFilter} onClose={() => setEditFilter(null)} />}
      {editLabel && <LabelDialog label={editLabel === "new" ? undefined : editLabel} onClose={() => setEditLabel(null)} />}
    </div>
  );
}

const HELP: [string, string][] = [
  ["today, tomorrow, overdue, no date", "By date"], ["7 days, this week, next week", "Date ranges"],
  ["due before: friday, due after: 1 dec", "Before or after a date"], ["p1 … p4", "Priority"],
  ["#Work, ##Work", "Project (## includes sub-projects)"], ["/Waiting", "Section"], ["@email, no labels", "Labels (* is a wildcard)"],
  ["assigned to: me, assigned to: Sam", "Assignee"], ["subtask, recurring, has deadline", "Task type"], ["search: invoice", "Text"],
  ["& | ! ( )", "And, or, not, brackets"], [",", "Show several lists"],
];

export function FilterDialog({ filter, onClose, initialQuery = "" }: { filter?: Filter; onClose: () => void; initialQuery?: string }) {
  const [name, setName] = useState(filter?.name ?? "");
  const [query, setQuery] = useState(filter?.query ?? initialQuery);
  const [color, setColor] = useState(filter?.color ?? "charcoal");
  const [favorite, setFavorite] = useState(filter?.isFavorite ?? false);
  const [help, setHelp] = useState(false);
  const ctx = useFilterContext();
  const tasks = useActiveTasks() ?? [];
  const navigate = useNavigate();
  const error = ctx && query.trim() ? validateFilter(query, ctx) : null;
  const preview = useMemo(() => {
    if (!ctx || !query.trim() || error) return null;
    try { return runFilter(query, tasks, ctx).flatMap((g) => g.tasks); } catch { return null; }
  }, [ctx, query, tasks, error]);

  const save = async () => {
    if (!name.trim() || !query.trim() || error) return;
    if (filter) await dispatch(cmd("filter_update", { id: filter.id, name: name.trim(), query: query.trim(), color, isFavorite: favorite }));
    else {
      const id = uuid();
      await dispatch(cmd("filter_add", { id, name: name.trim(), query: query.trim(), color, isFavorite: favorite }));
      navigate(`/filter/${id}`);
    }
    onClose();
  };

  return (
    <Dialog title={filter ? "Edit filter" : "Add filter"} size="lg" onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim() || !query.trim() || !!error} onClick={() => void save()}>Save</Button></>}>
      <div className="space-y-5">
        <Field label="Name" htmlFor="fd-name"><Input id="fd-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Urgent work" /></Field>
        <Field label="Query" htmlFor="fd-query" error={error}>
          <Input id="fd-query" className="font-mono" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="(today | overdue) & #Work" />
        </Field>
        {preview && (
          <div className="rounded-card border border-border bg-surface">
            <div className="flex items-center border-b border-border px-3 py-2 text-meta text-text-muted">
              <span className="font-semibold text-text">Preview</span><span className="tabular ml-auto">{preview.length} matching task{preview.length === 1 ? "" : "s"}</span>
            </div>
            {preview.length ? (
              <ul className="py-1">
                {sortTasks(preview).slice(0, 5).map((t) => {
                  const due = dueLabel(t);
                  return (
                    <li key={t.id} className="flex items-center gap-2.5 px-3 py-1.5 text-body">
                      <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full border-[1.5px] border-border-strong" />
                      <span className="min-w-0 flex-1 truncate">{t.content}</span>
                      {due && <span className={cx("shrink-0 text-meta", due.cls)}>{due.text}</span>}
                    </li>
                  );
                })}
              </ul>
            ) : <p className="px-3 py-3 text-body text-text-faint">Nothing matches yet.</p>}
          </div>
        )}
        <div><span className="mb-1.5 block text-meta font-medium text-text-muted">Colour</span><ColorPicker value={color} onChange={setColor} /></div>
        <Switch label="Add to favourites" checked={favorite} onChange={setFavorite} />
        <div className="rounded-card bg-surface-sunken">
          <button type="button" onClick={() => setHelp((h) => !h)} aria-expanded={help} className="flex w-full items-center gap-1.5 px-3 py-2.5 text-body font-medium phone:min-h-11">
            {help ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}Query help
          </button>
          {help && (
            <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 px-3 pb-3">
              {HELP.map(([k, v]) => <div key={k} className="contents"><dt className="break-words font-mono text-meta">{k}</dt><dd className="text-meta text-text-muted">{v}</dd></div>)}
            </dl>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function LabelDialog({ label, onClose }: { label?: Label; onClose: () => void }) {
  const [name, setName] = useState(label?.name ?? "");
  const [color, setColor] = useState(label?.color ?? "charcoal");
  const [favorite, setFavorite] = useState(label?.isFavorite ?? false);
  const save = async () => {
    const n = name.trim().replace(/^@/, "").replace(/\s+/g, "_");
    if (!n) return;
    if (label) await dispatch(cmd("label_update", { id: label.id, name: n, color, isFavorite: favorite }));
    else await dispatch(cmd("label_add", { id: uuid(), name: n, color, isFavorite: favorite }));
    onClose();
  };
  return (
    <Dialog title={label ? "Edit label" : "Add label"} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim()} onClick={() => void save()}>Save</Button></>}>
      <div className="space-y-5">
        <Field label="Name" htmlFor="ld-name"><Input id="ld-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void save(); }} placeholder="e.g. phone" /></Field>
        <div><span className="mb-1.5 block text-meta font-medium text-text-muted">Colour</span><ColorPicker value={color} onChange={setColor} /></div>
        <Switch label="Add to favourites" checked={favorite} onChange={setFavorite} />
      </div>
    </Dialog>
  );
}

export function LabelView() {
  const { id = "" } = useParams();
  const label = useLiveQuery(async () => (await db.labels.get(id)) ?? null, [id]);
  const tasks = useLiveQuery(() => db.tasks.filter((t) => !t.completedAt && t.labelIds.includes(id)).toArray(), [id], []);
  const [editing, setEditing] = useState(false);
  if (label === undefined) return <div className="h-[34px]" />;
  if (!label) return <EmptyState kind="label" title="Label not found">It may have been deleted.</EmptyState>;
  return (
    <div>
      <PageHeader leading={<Tag size={22} aria-hidden style={{ color: colorHex(label.color) }} />} title={`@${label.name}`} subtitle={`${tasks.length} open task${tasks.length === 1 ? "" : "s"}`}
        actions={<Menu label="Label actions" items={[
          { label: "Edit label", icon: Pencil, onSelect: () => setEditing(true) },
          { label: label.isFavorite ? "Remove from favourites" : "Add to favourites", icon: label.isFavorite ? StarOff : Star, onSelect: () => void dispatch(cmd("label_update", { id, isFavorite: !label.isFavorite })) },
        ]} />} />
      <TaskList tasks={sortTasks(tasks)} showProject addButton={false} emptyKind="label" emptyTitle="No open tasks with this label" emptyText={`Add @${label.name} to a task in quick add.`} />
      {editing && <LabelDialog label={label} onClose={() => setEditing(false)} />}
    </div>
  );
}

export function FilterView() {
  const { id = "" } = useParams();
  const saved = useLiveQuery(async () => (await db.filters.get(id)) ?? null, [id]);
  const builtin = BUILTIN_FILTERS.find((f) => f.id === id);
  const filter = builtin ?? saved;
  const ctx = useFilterContext();
  const tasks = useActiveTasks() ?? [];
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const result = useMemo(() => {
    if (!filter || !ctx) return null;
    try { return { groups: runFilter(filter.query, tasks, ctx), error: null }; } catch (e) { return { groups: [], error: (e as Error).message }; }
  }, [filter, ctx, tasks]);

  if (saved === undefined && !builtin) return <div className="h-[34px]" />;
  if (!filter) return <EmptyState kind="search" title="Filter not found">It may have been deleted.</EmptyState>;
  const multi = (result?.groups.length ?? 0) > 1;
  return (
    <div>
      <PageHeader leading={<span aria-hidden className="h-3 w-3 shrink-0 rounded-full" style={{ background: colorHex(filter.color) }} />} title={filter.name}
        subtitle={<span className="font-mono text-meta text-text-faint">{filter.query}</span>}
        actions={builtin
          ? <Button size="sm" icon={Plus} onClick={() => setSaving(true)}>Save a copy</Button>
          : <Menu label="Filter actions" items={[
            { label: "Edit filter", icon: Pencil, onSelect: () => setEditing(true) },
            { label: saved?.isFavorite ? "Remove from favourites" : "Add to favourites", icon: saved?.isFavorite ? StarOff : Star, onSelect: () => void dispatch(cmd("filter_update", { id, isFavorite: !saved?.isFavorite })) },
            { label: "Delete filter", icon: Trash2, danger: true, divider: true, onSelect: async () => { if (await confirmDialog({ title: `Delete “${filter.name}”?`, confirmLabel: "Delete", danger: true })) { void dispatch(cmd("filter_delete", { id })); showToast("Filter deleted"); history.back(); } } },
          ]} />} />
      {result?.error && <p className="mb-4 rounded-card bg-danger-soft px-3 py-2 text-body text-danger">{result.error}</p>}
      {result?.groups.map((g) => (
        <section key={g.name} className="mb-8">
          {multi && <SectionHeader title={g.name} count={g.tasks.length} />}
          <TaskList tasks={sortTasks(g.tasks)} showProject addButton={false} emptyKind="filters" emptyTitle="No matching tasks" />
        </section>
      ))}
      {editing && saved && <FilterDialog filter={saved} onClose={() => setEditing(false)} />}
      {saving && <FilterDialog initialQuery={filter.query} onClose={() => setSaving(false)} />}
    </div>
  );
}
